import { useEffect, useMemo, useRef, useState } from 'react';
import { useLoaderData, useNavigate, useNavigation } from '@remix-run/react';
import 'videojs-contrib-ads/dist/videojs.ads.css?url';
import videojs from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-ima';
import 'video.js/dist/video-js.css?url';
import 'videojs-ima/dist/videojs.ima.css?url';
import 'videojs-mux';
// @ts-ignore
import videojsContribEme from 'videojs-contrib-eme';
// @ts-ignore
import youbora from 'youboralib';
import 'youbora-adapter-videojs';
import type Player from 'video.js/dist/types/player';
import isEmpty from 'lodash.isempty';

import continueWatchingPlugin from './plugins/continue-watching.ts';
import playNextPlugin from './plugins/play-next.ts';
import analyticsPlugin from './plugins/analytics.ts';
import topBarPlugin from './plugins/top-bar.ts';
import jwAnalyticsPlugin from './plugins/jw-analytics';
import chapterMarkersPlugin from './plugins/chapter-markers.ts';
import muxAnalyticsPlugin from './plugins/mux-analytics.ts';
import brazeAnalyticsPlugin from './plugins/braze-analytics.ts';
import pianoAnalyticsPlugin from './plugins/piano-analytics.ts';
import brightcoveAnalyticsPlugin from './plugins/brightcove-analytics';
import persistedSettingsPlugin from './plugins/persisted-settings.ts';
import { PlayerPlugin } from './types.ts';
import { trackPlayerError, trackPlayerSessionEnd } from './tracking.ts';
import {
  isLive,
  getBoolValue,
  getMuxVideoAnalyticsParams,
  getPersistedPlayerSettings,
  disposePlayerAndNavigate,
} from './utils/index.ts';
import { drmSetup } from './drm/drm-setup.ts';
import type { VideojsOptions } from './types.ts';
import type { Feed } from '~/services/layout/index.ts';
import type { PlayerTrackData } from './tracking.ts';
import type { NavigateFunction } from '@remix-run/react';
import type {
  AnalyticsSharedVars,
  MuxAnalyticsProps,
  BrazeAnalyticsConfiguration,
} from '~/services/analytics/events-names.ts';
import { PlayNext } from './play-next.tsx';
import {
  getMidrollOffsetFromXml,
  isPostrollBreakIdInXml,
  videojsAdvertisement,
} from './advertisement/index.ts';
import { AdsBackButton } from './ads-back-button.tsx';
import { useIsGoogleIma } from './advertisement/use-is-google-ima.ts';
import {
  setFullScreenButton,
  fullScreenChangeListener,
} from './utils/fullScreenButton.ts';
import { useHideUserWayIcon } from '~/hooks/use-hide-userway-icon.ts';
import { useRedirectOnVideoEnd } from './hooks/use-redirect-on-video-end.ts';
import { pianoAnalytics } from '~/services/analytics/piano-analytics';
import { type CreateRequest } from '~/services/pipes/create-request.ts';
import { requestJsonData } from '~/utils/request-json-data.ts';
import { isValidUrl } from '~/utils/validations.ts';

const playerID = 'vjs_video_3';

export type LoaderData = {
  isMock: boolean;
  analyticsSharedVars: AnalyticsSharedVars;
  isContinueWatchingSyncedFeed: boolean;
  continueWatchingUserIdentifier: string;
  continueWatchingRemoteEventsRequest: CreateRequest;
  screenId: string;
  accountId: string;
  policyKey: string;
  viewerId: string;
  styles: any;
  muxAnalytics: MuxAnalyticsProps;
  brazeAnalytics: BrazeAnalyticsConfiguration;
  baseURL: string;
};

export default function VideojsPlayer({
  playableItem,
  pluginsData,
  appData,
  isNavigatingBack,
  playNextUrl,
  vmapXml,
}: {
  playableItem: any;
  videoId?: string;
  pluginsData: any;
  appData: any;
  isNavigatingBack: boolean;
  playNextUrl: string | null;
  vmapXml: string;
}) {
  const {
    analyticsSharedVars,
    isMock,
    isContinueWatchingSyncedFeed,
    continueWatchingUserIdentifier,
    continueWatchingRemoteEventsRequest,
    styles,
    muxAnalytics,
    brazeAnalytics,
    baseURL,
  } = useLoaderData<LoaderData>();

  const [isRunningPlayNext, setIsRunningPlayNext] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [currentPosition, setCurrentPosition] = useState<number>(0);
  const [playNextOverlayDuration, setPlayNextOverlayDuration] =
    useState<number>(0);
  const [playNextFeed, setPlayNextFeed] = useState<Feed | null>(null);
  const [showPlayNextOnPostroll, setShowPlayNextOnPostroll] =
    useState<boolean>(false);
  const [showAdsBackButton, setShowAdsBackButton] = useState<boolean>(false);

  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const youboraRef = useRef<any | null>(null);

  const playNextFeedRef = useRef<Feed | null>(null);
  const isRunningPlayNextRef = useRef(false);
  const closePlayNextRef = useRef<boolean>(false);

  const playerVolume = useRef<number | undefined | null>(null);
  const playerMuted = useRef<boolean | undefined | null>(null);

  // Live metadata tracking refs
  const liveMetadataTimeupdateHandlerRef = useRef<(() => void) | null>(null);
  const currentAssetIdRef = useRef<string | null>(null);
  const lastMetadataFetchTimeRef = useRef<number>(0);

  const handleTextTracks = (tracks: any) => {
    const persistedSettings = getPersistedPlayerSettings();

    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].kind === 'captions' || tracks[i].kind === 'subtitles') {
        if (!playNextFeedRef.current) {
          tracks[i].mode = 'disabled';
          return;
        }
        if (persistedSettings?.captionsEnabled) {
          const isSelectedTrack =
            tracks[i].language === persistedSettings?.selectedTrack?.language ||
            tracks[i].label === persistedSettings?.selectedTrack?.label;

          tracks[i].mode = isSelectedTrack ? 'showing' : 'disabled';
        } else {
          tracks[i].mode = 'disabled';
        }
      }
    }
  };

  playNextFeedRef.current = playNextFeed;
  isRunningPlayNextRef.current = isRunningPlayNext;

  const navigate: NavigateFunction = useNavigate();
  const navigation = useNavigation();
  const streamType = useMemo(
    () => (isLive(playableItem) ? 'live' : 'vod'),
    [playableItem]
  );
  const isFreeItem =
    playableItem.extensions?.free || playableItem.extensions?.isFree;
  const analyticsCustomProperties = useMemo(
    () =>
      playableItem.extensions?.analyticsCustomProperties &&
      JSON.stringify(playableItem.extensions?.analyticsCustomProperties),
    [playableItem.extensions?.analyticsCustomProperties]
  );

  const trackData: PlayerTrackData = useMemo(
    () => ({
      ...analyticsSharedVars,
      name: playableItem?.title,
      media_id: playableItem?.id,
      media_type: playableItem.type?.value,
      duration: playableItem.extensions?.duration,
      stream_format: playableItem?.content?.type,
      stream_type: streamType,
      free_or_paid: getBoolValue(isFreeItem) ? 'free' : 'paid',
      analyticsCustomProperties,
    }),
    [
      analyticsSharedVars,
      playableItem?.title,
      playableItem?.id,
      playableItem?.content?.type,
      playableItem.extensions?.duration,
      playableItem.type?.value,
      streamType,
      isFreeItem,
      analyticsCustomProperties,
    ]
  );

  const isPostroll: boolean = useMemo(() => {
    return isPostrollBreakIdInXml(vmapXml);
  }, []);

  const midrollTimeOffset: number = useMemo(() => {
    return getMidrollOffsetFromXml(vmapXml);
  }, []);

  const playNext = (event: any) => {
    if (!playNextUrl || closePlayNextRef.current) return;

    const isClickEvent: boolean = event?.type === 'click';

    if (isPostroll && !isClickEvent) return;

    const persistedSettings = getPersistedPlayerSettings();

    playerVolume.current = persistedSettings?.volume;
    playerMuted.current = persistedSettings?.muted;

    // use the link from the pre-loaded data to avoid pulling the play next feed URL twice
    const selfLink =
      (playNextFeed || playNextFeedRef.current)?.entry?.[0]?.link?.href ||
      playNextUrl;

    disposePlayerAndNavigate({
      player: playerRef.current as Player,
      isContinueWatchingSyncedFeed,
      navigationFunction: () => {
        window.location.href = `${baseURL}/player?self-link=${encodeURIComponent(
          selfLink
        )}`;
      },
    });
  };

  const isLiveRef = useRef<boolean>(false);

  const options: VideojsOptions = useMemo(() => {
    const playerOptions = {
      id: playerID,
      controls: true,
      controlBar: {
        volumePanel: {
          inline: false,
        },
        fullscreenToggle: !playNextUrl,
      },
      fill: true,
      autoplay: true,
      bigPlayButton: false,
      experimentalSvgIcons: true,
      liveui: true,
      plugins: {
        continueWatchingPlugin: {
          entryId: playableItem.id,
          resumeTime: playableItem.extensions?.resumeTime,
          entry: playableItem,
          isContinueWatchingSyncedFeed,
          continueWatchingUserIdentifier,
          continueWatchingRemoteEventsRequest,
          continueWatchingPluginConfiguration:
            pluginsData?.continueWatching?.customConfiguration,
          getIsLive: () => isLive(playableItem) || isLiveRef.current,
          baseURL,
        },
        playNextPlugin: {
          setIsRunningPlayNext,
          setPlayNextFeed,
          playNextFeedUrl: playNextUrl,
          setDuration,
          setCurrentPosition,
          playNext,
          isPostroll,
          playNextOverlayDuration,
        },
        analyticsPlugin: { analyticsSharedVars, trackData },
        topBarPlugin: {
          playerID,
          title: playableItem?.title,
          playableItem,
          analyticsTrackData: trackData,
          baseURL,
          isContinueWatchingSyncedFeed,
        },
        chapterMarkersPlugin: {
          playerID,
          playableItem,
          configuration: styles,
          setPlayNextOverlayDuration,
        },
        persistedSettingsPlugin: {
          playerID,
          navigation,
        },
      },
    };

    if (pluginsData?.jw_token) {
      // @ts-ignore
      playerOptions.plugins.jwAnalyticsPlugin = {
        token: pluginsData?.jw_token,
        userId: pluginsData?.jwUserId,
        trackData,
        playableItem,
      };
    }

    if (pluginsData?.pianoAnalytics?.enabled) {
      (playerOptions.plugins as Record<string, unknown>).pianoAnalyticsPlugin =
        {
          trackData,
          playableItem,
          enabled: pluginsData?.pianoAnalytics?.track_player_events,
          eventMapperConfig: pluginsData?.pianoAnalytics?.local_event_mapper,
        };
    }

    if (muxAnalytics?.enabled && muxAnalytics?.env_key) {
      const data = {
        env_key: muxAnalytics?.env_key,
        // Site Metadata
        viewer_user_id: pluginsData?.muxUserId,
        // Player Metadata
        player_name: playerID,
        // Video Metadata
        ...getMuxVideoAnalyticsParams(muxAnalytics, playableItem),
      };

      // @ts-ignore
      playerOptions.plugins.mux = {
        debug: false,
        data,
      };
    }

    if (brazeAnalytics?.enabled) {
      // @ts-ignore
      playerOptions.plugins.brazeAnalyticsPlugin = {
        brazeUserId: pluginsData?.brazeUserId,
        brazeAnalytics,
        trackData,
        playableItem,
      };
    }

    if (pluginsData?.brightcove_account_id) {
      // @ts-ignore
      playerOptions.plugins.brightcoveAnalyticsPlugin = {
        config: {
          accountId: pluginsData.brightcove_account_id,
          playerId: playerID,
          playerName: pluginsData.brightcoveAnalytics?.player_name,
          userId: pluginsData?.brightcoveUserId,
          enableQoE: pluginsData.brightcoveAnalytics?.enable_qoe,
          engagementUpdateInterval:
            pluginsData.brightcoveAnalytics?.engagement_update_interval,
        },
        trackData,
        playableItem,
      };
    }

    return playerOptions;
  }, [playableItem, playNextUrl, analyticsSharedVars, trackData, navigate]);

  useEffect(() => {
    return () => {
      trackPlayerSessionEnd(trackData, 0);
    };
  }, []);

  useEffect(() => {
    if (playNextUrl) {
      document.addEventListener('fullscreenchange', fullScreenChangeListener);

      return () => {
        document.removeEventListener(
          'fullscreenchange',
          fullScreenChangeListener
        );
      };
    }
  }, [playNextUrl]);

  useEffect(() => {
    if (navigation.state === 'loading') return;
    if (isNavigatingBack) return;

    const src = playableItem?.content?.src;
    const type = playableItem?.content?.type;

    const { drm } = playableItem.extensions || {};

    const hasDrmConfig = !isEmpty(drm);

    const videoElement = document.createElement('video-js');

    if (!videoElement) return;

    videoElement.classList.add('vjs-big-play-centered');

    const videoContainer = videoRef.current;

    if (!videoContainer) return;
    videoContainer.appendChild(videoElement);

    if (!videojs.getPlugin(PlayerPlugin.continueWatchingPlugin)) {
      videojs.registerPlugin(
        PlayerPlugin.continueWatchingPlugin,
        continueWatchingPlugin
      );
    }

    if (!videojs.getPlugin(PlayerPlugin.playNextPlugin)) {
      videojs.registerPlugin(PlayerPlugin.playNextPlugin, playNextPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.analyticsPlugin)) {
      videojs.registerPlugin(PlayerPlugin.analyticsPlugin, analyticsPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.topBarPlugin)) {
      videojs.registerPlugin(PlayerPlugin.topBarPlugin, topBarPlugin);
    }

    if (
      !videojs.getPlugin(PlayerPlugin.jwAnalyticsPlugin) &&
      pluginsData?.jw_token
    ) {
      videojs.registerPlugin(PlayerPlugin.jwAnalyticsPlugin, jwAnalyticsPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.chapterMarkersPlugin)) {
      videojs.registerPlugin(
        PlayerPlugin.chapterMarkersPlugin,
        chapterMarkersPlugin
      );
    }

    if (
      !videojs.getPlugin(PlayerPlugin.mux) &&
      muxAnalytics?.enabled &&
      muxAnalytics?.env_key
    ) {
      videojs.registerPlugin(PlayerPlugin.mux, muxAnalyticsPlugin);
    }

    if (!videojs.getPlugin(PlayerPlugin.persistedSettingsPlugin)) {
      videojs.registerPlugin(
        PlayerPlugin.persistedSettingsPlugin,
        persistedSettingsPlugin
      );
    }

    if (
      !videojs.getPlugin(PlayerPlugin.brazeAnalyticsPlugin) &&
      brazeAnalytics?.enabled
    ) {
      videojs.registerPlugin(
        PlayerPlugin.brazeAnalyticsPlugin,
        brazeAnalyticsPlugin
      );
    }

    if (
      !videojs.getPlugin(PlayerPlugin.pianoAnalyticsPlugin) &&
      pluginsData?.pianoAnalytics?.enabled
    ) {
      // Initialize Piano Analytics before registering the plugin
      try {
        const { collection_domain, site_id } = pluginsData.pianoAnalytics;

        if (!collection_domain || !site_id) {
          throw new Error(
            `Piano Analytics initialization failed. Missing configuration: collection_domain - ${collection_domain}, site_id - ${site_id}`
          );
        }

        pianoAnalytics.initialize({
          collectionDomain: collection_domain,
          siteId: site_id,
          enabled: pluginsData.pianoAnalytics.enabled,
        });
      } catch (error) {
        console.error(`[Piano Analytics] ${(error as Error).message}`);
      }

      videojs.registerPlugin(
        PlayerPlugin.pianoAnalyticsPlugin,
        pianoAnalyticsPlugin
      );
    }

    if (
      !videojs.getPlugin(PlayerPlugin.brightcoveAnalyticsPlugin) &&
      pluginsData?.brightcove_account_id
    ) {
      videojs.registerPlugin(
        PlayerPlugin.brightcoveAnalyticsPlugin,
        brightcoveAnalyticsPlugin
      );
    }
    playerRef.current = videojs(
      videoElement,
      {
        ...options,
        html5: {
          vhs: {
            overrideNative: !hasDrmConfig, // Force VHS on Safari if no DRM
          },
          nativeAudioTracks: hasDrmConfig, // Required for Safari DRM
          nativeVideoTracks: hasDrmConfig, // Required for Safari DRM
        },
        id: playerID,
      },
      () => {
        videojs.log('player is ready', { playableItem });

        playableItem.extensions?.play_next_feed_url &&
          setFullScreenButton(playerRef.current);
      }
    );

    const player = playerRef.current;

    if (typeof playerMuted.current === 'boolean') {
      player.muted(playerMuted.current);
    }
    if (typeof playerVolume.current === 'number') {
      player.volume(playerVolume.current);
    }

    if (typeof youbora !== 'undefined' && pluginsData?.youboraAccountCode) {
      youboraRef.current = new youbora.Plugin({
        accountCode: pluginsData?.youboraAccountCode,
      });
      const youboraAdapter = youboraRef.current;

      const cdnKey = pluginsData?.analyticsMapper?.cdn;

      // Get initial live metadata if available
      const liveMetadata = playableItem?.extensions?.liveMetadata;
      const liveMetadataConfig = playableItem?.extensions?.liveMetadataConfig;

      youboraAdapter.setOptions({
        'app.name': appData?.appName,
        'app.releaseVersion': appData?.appVersion,
        'content.isLive': playableItem?.extensions?.isLive,
        'content.title':
          liveMetadata?.assetId ||
          playableItem?.extensions?.guid ||
          playableItem?.title,
        'content.id': liveMetadata?.programId || playableItem?.id,
        'content.cdn': cdnKey
          ? playableItem?.[cdnKey]
          : playableItem?.extensions?.cdn,
        'user.name': pluginsData?.userId,
      }); // metadata options, https://documentation.npaw.com/integration-docs/docs/setting-options-and-metadata.

      youboraAdapter.setAdapter(new youbora.adapters.Videojs(player)); // Attach adapter

      // Initialize live metadata tracking if available
      if (
        liveMetadata &&
        liveMetadataConfig?.updateUrl &&
        typeof liveMetadataConfig?.updateInterval === 'number' &&
        isFinite(liveMetadataConfig?.updateInterval) &&
        liveMetadataConfig?.updateInterval > 0
      ) {
        currentAssetIdRef.current = liveMetadata.assetId || null;
        lastMetadataFetchTimeRef.current = Date.now();

        const updateInterval = liveMetadataConfig.updateInterval * 1000; // Convert to milliseconds

        const fetchLiveMetadata = async () => {
          try {
            if (!isValidUrl(liveMetadataConfig.updateUrl)) {
              videojs.log('Invalid live metadata update URL');
              return;
            }
            const data = await requestJsonData({
              url: liveMetadataConfig.updateUrl,
            });

            if (!data) {
              videojs.log('Failed to fetch live metadata');
              return;
            }

            const entry = data?.entry?.[0];
            const newLiveMetadata = entry?.extensions?.liveMetadata;

            if (
              newLiveMetadata &&
              newLiveMetadata.assetId !== currentAssetIdRef.current
            ) {
              // Program changed - update Youbora metadata
              currentAssetIdRef.current = newLiveMetadata.assetId;

              youboraAdapter.setOptions({
                'content.title': newLiveMetadata.assetId,
                'content.id': newLiveMetadata.programId,
              });

              videojs.log('Live metadata updated', {
                programId: newLiveMetadata.programId,
                title: newLiveMetadata.assetId,
              });
            }
          } catch (error) {
            videojs.log('Error fetching live metadata', error);
          }
        };

        const timeupdateHandler = () => {
          const now = Date.now();
          if (now - lastMetadataFetchTimeRef.current >= updateInterval) {
            lastMetadataFetchTimeRef.current = now;
            fetchLiveMetadata();
          }
        };

        player.on('timeupdate', timeupdateHandler);
        liveMetadataTimeupdateHandlerRef.current = timeupdateHandler;
      }
    }

    player.on('loadedmetadata', () => {
      const tracks: any = player.textTracks();
      handleTextTracks(tracks);

      // @ts-ignore
      const liveTracker = player.liveTracker;
      const isLive = liveTracker && liveTracker.isLive();

      isLiveRef.current = !!isLive;

      if (isLive) {
        // Seek to the live point
        player.currentTime(liveTracker.seekableEnd());
      }
    });

    // Hide context menu for player
    player?.on('contextmenu', function (e: any) {
      e?.preventDefault();
    });

    try {
      if (isMock) {
        player.src({
          src: require('../../mocks/data/movie.mp4'),
          type: 'video/mp4',
        });

        return;
      }
      if (drm) {
        if (!videojs.getPlugin('eme')) {
          videojs.registerPlugin('eme', videojsContribEme);
        }

        const sourceExtras = drmSetup(player, drm, type);

        player.src([{ src, ...sourceExtras }]);
      } else {
        player.src([{ src, type: 'application/x-mpegURL' }]);
      }
    } catch (error) {
      videojs.log('player failed to set video source', { playableItem });

      trackPlayerError(trackData, currentPosition || 0);
    }

    return () => {
      setIsRunningPlayNext(false);
      playNextFeedRef.current = null;
      isRunningPlayNextRef.current = false;
      isLiveRef.current = false;

      // Clean up live metadata tracking
      if (liveMetadataTimeupdateHandlerRef.current && player) {
        player.off('timeupdate', liveMetadataTimeupdateHandlerRef.current);
        liveMetadataTimeupdateHandlerRef.current = null;
      }
      currentAssetIdRef.current = null;
      lastMetadataFetchTimeRef.current = 0;

      if (!player) return;

      if (!document.pictureInPictureElement) player.dispose();
    };
  }, [options, playableItem]);

  const isGoogleImaSdkReady: boolean = useIsGoogleIma();

  useEffect(() => {
    if (!isGoogleImaSdkReady) return;

    videojsAdvertisement({
      playerID,
      player: playerRef.current as Player,
      vmapXml,
      midrollTimeOffset,
      isPostroll,
      setShowAdsBackButton,
      setShowPlayNextOnPostroll,
    });
  }, [options, playableItem, isGoogleImaSdkReady]);

  useHideUserWayIcon();

  useRedirectOnVideoEnd({
    player: playerRef.current as Player,
    playableItem,
    baseURL,
    isPlayNext: !!playNextUrl,
    isContinueWatchingSyncedFeed,
  });

  return (
    <div data-vjs-player className="player">
      {showAdsBackButton && <AdsBackButton playableItem={playableItem} />}
      <div
        ref={videoRef}
        className="h-screen overflow-y-hidden overscroll-none scrollbar-hide"
      />
      {isRunningPlayNext && playNextFeed && !closePlayNextRef.current && (
        <PlayNext
          playNext={playNext}
          feed={playNextFeed}
          totalDuration={duration}
          position={currentPosition}
          isPostroll={isPostroll}
          showPlayNextOnPostroll={showPlayNextOnPostroll}
          closePlayNextRef={closePlayNextRef}
        />
      )}
    </div>
  );
}
